import React, { useEffect, useRef } from "react";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import { MdOutgoingMail } from "react-icons/md";

function MessageModal({
  isOpen,
  draft,
  context,
  saving,
  onChange,
  onSend,
  onClose,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const title = context?.isGroupMessage
    ? `Message au groupe ${context.groupName || ""}`
    : `Message à ${context.adherentName || "un responsable"}`;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-purple-700 to-sky-600 p-5 text-white dark:border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-2xl backdrop-blur-xl">
                <MdOutgoingMail />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/65">
                  Messagerie
                </p>

                <h2 className="mt-2 font-titre text-2xl font-bold">
                  {title}
                </h2>

                <p className="mt-2 text-sm font-medium text-white/70">
                  {context.recipients.length} destinataire
                  {context.recipients.length > 1 ? "s" : ""}
                </p>
              </div>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Sujet
            </span>

            <input
              type="text"
              value={draft.subject}
              onChange={(event) => onChange("subject", event.target.value)}
              placeholder="Sujet du message"
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-purple-300 focus:ring-4 focus:ring-purple-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-purple-300/50 dark:focus:ring-purple-500/10"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Message
            </span>

            <textarea
              value={draft.content}
              onChange={(event) => onChange("content", event.target.value)}
              rows={7}
              placeholder="Votre message..."
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-7 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-purple-300 focus:ring-4 focus:ring-purple-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-purple-300/50 dark:focus:ring-purple-500/10"
            />
          </label>
        </div>

        <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="button"
            onClick={onSend}
            disabled={saving}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-purple-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
          >
            <FaPaperPlane />
            {saving ? "Envoi..." : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MessageModal;